import { Link } from 'react-aria-components';
import { contrastRatio, effectiveAccent } from '@tallyframe/core';
import type { ReactElement } from 'react';
import { sectionPath, type NavSection } from '../app/nav';
import { SectionIcon } from './SectionIcon';
import { useBrand } from '../brandContext';
import { Icon } from '../ui/Icon';
import { Mark } from '../components/Mark';
import { IconButton } from '../ui/IconButton';
import { Tooltip } from '../ui/Tooltip';
import { AccountMenu } from './AccountMenu';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';

export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return words
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
}

const BADGE_WHITE = '#FFFFFF'; // --rp-ui-on-nav-strong
const BADGE_INK = '#18181B'; // --rp-ui-ink

/** White (on-nav-strong) or ink, whichever has the higher WCAG contrast on the accent. Class names stay literal for Tailwind. */
export function badgeForeground(accent: string): { tone: 'white' | 'ink'; hex: string; className: string } {
  return contrastRatio(accent, BADGE_INK) > contrastRatio(accent, BADGE_WHITE)
    ? { tone: 'ink', hex: BADGE_INK, className: 'text-ink' }
    : { tone: 'white', hex: BADGE_WHITE, className: 'text-on-nav-strong' };
}

/** The workspace's initials on its brand colour (a runtime value, hence the inline style). */
function WorkspaceBadge({ name }: { name: string }): ReactElement {
  const { brand } = useBrand();
  const background = effectiveAccent({ accent: brand?.accent ?? null });
  const foreground = badgeForeground(background);
  return (
    <span
      className={`inline-flex items-center justify-center h-8 w-8 rounded-control text-sm font-ui ${foreground.className}`}
      style={{ background }}
    >
      {initialsOf(name)}
    </span>
  );
}

/** Layout D's 56px rail: the sidebar's collapsed form. */
export function Rail({
  workspaceId,
  sections,
  activeSection,
  onExpand
}: {
  workspaceId: string;
  sections: NavSection[];
  activeSection: string | null;
  onExpand(): void;
}): ReactElement {
  const main = sections.filter((s) => s.placement === 'main');
  const bottom = sections.filter((s) => s.placement === 'bottom');
  const sectionLink = (s: NavSection): ReactElement => (
    <Tooltip key={s.id} label={s.label}>
      <Link
        href={sectionPath(s, workspaceId)}
        aria-label={s.label}
        aria-current={s.id === activeSection ? 'page' : undefined}
        className="inline-flex items-center justify-center h-8 w-8 rounded-control text-on-nav-default hover:bg-on-nav-hover current:bg-fill current:text-on-fill focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-on-nav-strong"
      >
        <SectionIcon name={s.icon} />
      </Link>
    </Tooltip>
  );
  return (
    <nav aria-label="Main" className="w-rail bg-nav text-on-nav-default font-ui flex flex-col items-center gap-2 py-3 sticky top-0 h-screen overflow-y-auto">
      {sections[0] ? (
        <Link
          href={sectionPath(sections[0], workspaceId)}
          aria-label="ReportPad home"
          className="inline-flex items-center justify-center rounded-control no-underline text-on-nav-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-on-nav-strong"
        >
          <Mark size={24} tone="dark" />
        </Link>
      ) : (
        <Mark size={24} tone="dark" />
      )}
      <IconButton label="Expand sidebar" surface="dark" onPress={onExpand}>
        <Icon name="menu" size={16} />
      </IconButton>
      <WorkspaceSwitcher workspaceId={workspaceId} variant="rail" badge={(name) => <WorkspaceBadge name={name} />} />
      {main.map(sectionLink)}
      <div className="grow" data-rail-spacer="" />
      {bottom.map(sectionLink)}
      <AccountMenu compact />
    </nav>
  );
}
